import { Badge, createStyles, Group, Text, Tooltip } from '@mantine/core';
import { isEmpty, omit } from 'lodash/fp';
import React from 'react';

import { StoreListing } from '@portals/api/partners';
import { OverflowGroup } from '@portals/core';
import { formatCurrency } from '@portals/utils';

import { useStoreListingsTableContext } from './StoreListingTableContextProvider';

interface StorePricesCellProps {
  storeListing: StoreListing;
}

export function StorePricesCell({ storeListing }: StorePricesCellProps) {
  const { classes } = useStyles();
  const { currency } = useStoreListingsTableContext();

  const price = storeListing.prices.find(
    (price) => price.currency === currency
  );

  if (!price || isEmpty(omit('currency', price))) {
    return null;
  }

  const items: Array<{
    id: 'one_time' | 'monthly' | 'yearly' | 'usage_based';
    value: string;
  }> = [];

  if (price.one_time_price_in_scu) {
    const oneTimePrice = formatCurrency(price.one_time_price_in_scu, currency);

    items.push({
      id: 'one_time',
      value: oneTimePrice,
    });
  }

  if (price.monthly_price_in_scu) {
    const monthlyPrice = formatCurrency(price.monthly_price_in_scu, currency);

    items.push({
      id: 'monthly',
      value: `${monthlyPrice} / month`,
    });
  }

  if (price.yearly_price_in_scu) {
    const yearly = formatCurrency(price.yearly_price_in_scu, currency);

    items.push({
      id: 'yearly',
      value: `${yearly} / year`,
    });
  }

  if (price.usage_based_price_in_scu) {
    const usageBased = formatCurrency(price.usage_based_price_in_scu, currency);

    items.push({
      id: 'usage_based',
      value: `${usageBased} / ${storeListing.product.usage_based_unit_name}`,
    });
  }

  return (
    <Group pr="xs">
      <OverflowGroup
        overflowRenderer={({ hiddenIndexes }) => (
          <Tooltip
            withArrow
            position="bottom"
            label={
              <>
                {hiddenIndexes.map((index) => (
                  <Text key={items?.[index]?.id}>{items?.[index]?.value}</Text>
                ))}
              </>
            }
          >
            <Badge size="lg" className={classes.badge} mr={15}>
              + {hiddenIndexes?.length}
            </Badge>
          </Tooltip>
        )}
      >
        <Group sx={{ overflow: 'hidden' }} noWrap spacing="xs">
          {items?.map(({ id, value }) => (
            <Badge size="lg" className={classes.badge} key={id}>
              {value}
            </Badge>
          ))}
        </Group>
      </OverflowGroup>
    </Group>
  );
}

const useStyles = createStyles((theme) => ({
  badge: {
    overflow: 'unset',
    textOverflow: 'unset',
    backgroundColor: theme.colors.gray[2],
    color: theme.colors.gray[8],
    textTransform: 'unset',
    fontWeight: 500,
  },
}));
